import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { API, LocalStorageKeys } from "../utils/constant";
import { getFromStorage, removeAllFromStorage, saveOnStorage } from "../utils/helpers";
import { refreshAccessToken } from "./apiService";
import { IPollingResponse, IRefreshTokenResponse } from "../types/login.types";
import store from "../redux/store";
import { setObjectDetails } from "../redux/reducers/objectSlice";

interface IAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

export const coreApiInstance = createAxiosInstance(API.BASE_URL);

function createAxiosInstance(baseURL: string) {
  const axiosInstance = axios.create({
    baseURL,
  });

  // Load tokens from local storage if they exist
  const credentials: IPollingResponse | null = getFromStorage(LocalStorageKeys.CREDENTIALS);
  if (credentials) {
    axiosInstance.defaults.headers["instanceUrl"] = credentials.instance_url;
    axiosInstance.defaults.headers["accesstoken"] = credentials.access_token;
  }

  axiosInstance.interceptors.request.use(
    (config: any) => {
      if (!(config?.url == API.TOKEN_POLLING || config?.url == API.REQUEST_TOKEN))
        // Dispatch the action to indicate loading has started
        store.dispatch(setObjectDetails({ isLoading: true }));
      return config;
    },
    (error) => {
      // Handle request errors here
      store.dispatch(setObjectDetails({ isLoading: false }));
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      // Dispatch the action to indicate loading has finished
      store.dispatch(setObjectDetails({ isLoading: false }));
      return response;
    },
    async (error: AxiosError) => {
      // console.log("error on axios instance", error);
      const originalRequest: IAxiosRequestConfig = error.config;
      if (
        (error?.response?.status === 401 || error?.response?.status === 400 || (error?.response?.data as any)?.errorCode === "INVALID_SESSION_ID") &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          // console.log("authentication error");
          let credentials: IPollingResponse = await getFromStorage(LocalStorageKeys.CREDENTIALS);
          if (!credentials) {
            store.dispatch(setObjectDetails({ isLoading: false }));
            return;
          }
          if (originalRequest.url === API.REFRESH_TOKEN) {
            removeAllFromStorage()
            window.location.replace('/login');
            store.dispatch(setObjectDetails({ isLoading: false }));
            return;
          }
          const resp = await refreshAccessToken({ refresh_token: credentials.refresh_token });
          const { access_token, instance_url } = resp.data;
          if (access_token && instance_url) {
            setAccessTokenToHeader(access_token, instance_url);
            credentials.access_token = resp.data.access_token;
            credentials.signature = resp.data.signature;
            credentials.scope = resp.data.scope;
            credentials.instance_url = resp.data.instance_url;
            credentials.id = resp.data.id;
            credentials.token_type = resp.data.token_type;
            credentials.issued_at = resp.data.issued_at;
            saveOnStorage(LocalStorageKeys.CREDENTIALS, credentials);
            originalRequest.headers["accesstoken"] = `${access_token}`;
            originalRequest.headers["instanceurl"] = `${instance_url}`;
            return coreApiInstance(originalRequest);
          }
        } catch (err) {
          // console.log("something wrong on axios instance", err);
          // Redirect immediately without showing the error page
          // removeAllFromStorage()
          // window.location.replace('/login');
          store.dispatch(setObjectDetails({ isLoading: false }));
          return;  // Prevent any further processing
        }
      }

      if (
        error?.response?.status === 502 &&
        (error?.response?.data as any)?.message === "Internal server error" &&
        !originalRequest?._retry
      ) {
        originalRequest._retry = true;
        return coreApiInstance(originalRequest);
      }
      store.dispatch(setObjectDetails({ isLoading: false }));
      return Promise.reject(error); // Other errors will still be handled normally
    }
  );

  return axiosInstance;
}

export const coreRequests = {
  get: <T>(url: string, config?: any): Promise<AxiosResponse<T>> => coreApiInstance.get<T>(url, config),
  post: <T>(url: string, body: any): Promise<AxiosResponse<T>> => coreApiInstance.post<T>(url, body),
  patch: <T>(url: string, body: any): Promise<AxiosResponse<T>> => coreApiInstance.patch<T>(url, body),
  put: <T>(url: string, body: any): Promise<AxiosResponse<T>> => coreApiInstance.put<T>(url, body),
  delete: <T>(url: string): Promise<AxiosResponse<T>> => coreApiInstance.delete<T>(url),
};

export function setAccessTokenToHeader(accessToken: string, instanceUrl: string) {
  coreApiInstance.defaults.headers["instanceUrl"] = instanceUrl;
  coreApiInstance.defaults.headers["accesstoken"] = accessToken;
}
